import { useContext } from 'react';

import { Field, Image, ImageField, Link, LinkField, RichText, Text } from '@sitecore-jss/sitecore-jss-nextjs';

import { ModalContext } from 'src/context/Modal/ModalContext';
import { RcnUserContext } from 'src/context/RcnUser/RcnUserContext';

import useContentAccessLevel from 'src/hooks/useContentAccessLevel';

import { ComponentProps } from 'src/lib/ComponentProps';

import { parseAccessLevelContent } from 'src/mappers/fields/parseAccessLevelContent';

import { ContentAccessTypeItem } from 'src/types/ContentAccessTypeItem';

import ChevronDownIconSvg from 'public/icons/ChevronDown.svg';

import Accordion from '../_Accordion/Accordion';

import styles from './InformationPanels.module.css';

type InformationPanelsProps = ComponentProps & {
    fields: {
        Title: Field<string>;
        Description: Field<string>;
        Link?: LinkField;
        LinkIcon?: ImageField;
        AccessType: Array<ContentAccessTypeItem>;
        items: {
            fields: {
                Title: Field<string>;
                Icon: ImageField;
                Content: Field<string>;
            }
        }[];
        __Updated: Field<string>;
    };
};

export default function InformationPanels(props: InformationPanelsProps): JSX.Element {
    const RcnUser = useContext(RcnUserContext);
    const access = parseAccessLevelContent(props.fields?.AccessType);
    const contentAccessLevel = useContentAccessLevel(access);
    const ModalState = useContext(ModalContext);
    
    const handleAccordionToggle = (active: boolean, setActive: (active: boolean) => void) => {
		if (RcnUser.authenticatedUserProfile) {
            if (!contentAccessLevel.isAllowedAccessToContent) {
                setActive(false);
                ModalState.updateModalInfo({
                    LoginFormModal: {
                        Display: true,
                        Data: { contentAccessLevel: contentAccessLevel },
                    },
                })
            } else {
                setActive(!active);
            }
        } else {
            setActive(false);
            ModalState.updateDisplayLoginFormModal(true);
        }
    };

    return (
        <section className="maxWidth pad">
            <div className={styles.descriptionContainer}>
                <Text tag="h1" field={props.fields.Title} className={styles.componentTitle}/>
                <RichText field={props.fields.Description} className={styles.componentDescription} />

                {props.fields.Link?.value && (
                    <div className={styles.linkContainer}>
                        <Image field={props.fields.LinkIcon} className={styles.linkIcon} />
                        <Link field={props.fields.Link} className={styles.link}/>
                    </div>
                )}
            </div>
            <div className="maxWidth">
                {props.fields.items?.map((item, index) => (
                    <Accordion
                        key={index} 
                        sectionClass={styles.accordion}
                        headerClass={styles.accordionHeader}
                        bodyClass={styles.accordionBody}
                        onClick={(active, setActive) => handleAccordionToggle(active, setActive)}
                    >
                        <>
                            <span className={styles.accordionHeaderContent}>
                                <Image className={styles.icon} media={item.fields.Icon} alt={item.fields.Icon.value?.alt} />
                                <Text field={item.fields.Title} />
                            </span>
                            <span>
                                <ChevronDownIconSvg className={styles.chevron} />
                            </span>
                        </>
                        <>
                            {contentAccessLevel.isAllowedAccessToContent && (
                                <RichText field={item.fields.Content} />
                            )}
                        </>
                    </Accordion>
                ))}
            </div>
        </section>
    );
}
